import type { GeoJsonObject } from 'geojson';
import { GeoJSON as GeoJSONEntity, Map, TileLayer as TileLayerEntity } from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { GeoJSON, MapContainer, TileLayer } from 'react-leaflet';
import Control from 'react-leaflet-custom-control';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/lib/components/Button.component';
import { Checkbox } from 'src/lib/components/Checkbox.component';
import { InputFile } from 'src/lib/components/InputFile.component';
import { Select } from 'src/lib/components/Select.component';
import { TextArea } from 'src/lib/components/TextArea.component';
import { TextInput } from 'src/lib/components/TextInput.component';
import { toasting } from 'src/lib/components/Toast.component';
import { fdelete, fget, fput, getErrorMessage } from 'src/lib/fetch';
import { SelectBoxBaseLayer } from 'src/modules/map-layers/components/SelectBoxBaseLayer.component';
import { MAPBOX_TILE_LAYER_PROPS } from 'src/redux/slices/baseLayer.slice';
import { RiverDeviceMarkerLayer } from './RiverDeviceMarkerLayer.component';
import { RiverLineDrawingLayer } from './RiverLineDrawingLayer.component';
import {
  createRiverGeoJsonFilename,
  createRiverLineFeatureCollection,
  extractRiverLinePaths,
  fitMapToRiverLine,
  getDefaultActiveRiverLineSelection,
  getTotalRiverPointCount,
  isHydroVenusDevice,
  parseDeviceCoordinate,
  stringifyRiverGeoJson,
  type RiverDeviceMarker,
  type RiverLineCoordinate,
  type RiverLinePath,
  type RiverLineSelection,
} from './river-geojson';
import { AreaEntity } from '../../dto/area/area.entity';
import { ResponseDocument, ResponseGetDocument, ResponseListDocuments } from '../../dto/response';
import { RiverEntity } from '../../dto/river/river.entity';
import { DeleteRiverReq } from '../../dto/river/river.req';

const EMPTY_GEOJSON = createRiverLineFeatureCollection([]);

interface DeviceDocument {
  DEVICE_ID?: { S?: string };
  DEVICE_NAME?: { S?: string };
  DEVICE_TYPE?: { S?: string };
  DEVICE_COORDINATE?: { S?: string };
  AREA_NAME?: { S?: string };
}

export function EditRiverPage() {
  const eForm = useRef<HTMLFormElement>(null);
  const eTextName = useRef<HTMLInputElement>(null);
  const eSelectAreaName = useRef<HTMLSelectElement>(null);
  const eInputFile = useRef<HTMLInputElement>(null);
  const eTextAreaJson = useRef<HTMLTextAreaElement>(null);
  const baseTileLayer = useRef<TileLayerEntity>(new TileLayerEntity(''));
  const map = useRef<Map | null>(null);
  const eGeoJSON = useRef<GeoJSONEntity | null>(null);
  const eButtonSubmit = useRef<HTMLButtonElement>(null);
  const eButtonDelete = useRef<HTMLButtonElement>(null);
  const eButtonModeEdit = useRef<HTMLInputElement>(null);

  const [checkedModeEdit, setCheckedModeEdit] = useState(false);
  const [drawingEnabled, setDrawingEnabled] = useState(false);
  const [geoJsonStatus, setGeoJsonStatus] = useState<'empty' | 'loading' | 'success' | 'error'>('empty');
  const [geoJsonData, setGeoJsonData] = useState<GeoJsonObject>(EMPTY_GEOJSON);
  const [linePaths, setLinePaths] = useState<RiverLinePath[]>([]);
  const [selectedRegionName, setSelectedRegionName] = useState('');
  const [areaDevices, setAreaDevices] = useState<RiverDeviceMarker[]>([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [activeSelection, setActiveSelection] = useState<RiverLineSelection | null>(null);
  const [lineHistory, setLineHistory] = useState<RiverLinePath[][]>([]);
  const [data, setData] = useState<{
    river?: ResponseDocument<RiverEntity>;
    areas?: ResponseListDocuments<AreaEntity>;
  }>();

  const navigate = useNavigate();
  const { id } = useParams();

  const syncGeoJsonPreview = (
    nextGeoJson: GeoJsonObject,
    nextPaths = extractRiverLinePaths(nextGeoJson),
    options?: { fitMap?: boolean },
  ) => {
    setGeoJsonData(nextGeoJson);
    setLinePaths(nextPaths);

    eGeoJSON.current?.clearLayers();
    eGeoJSON.current?.addData(nextGeoJson);

    if (eTextAreaJson.current) {
      eTextAreaJson.current.value = stringifyRiverGeoJson(nextGeoJson);
    }

    if (options?.fitMap) {
      fitMapToRiverLine(map.current, nextPaths);
    }

    setGeoJsonStatus(nextPaths.some((path) => path.length >= 2) ? 'success' : 'empty');
  };

  const applyLinePaths = (
    nextPaths: RiverLinePath[],
    options?: { fitMap?: boolean; activeSelection?: RiverLineSelection | null; resetHistory?: boolean },
  ) => {
    syncGeoJsonPreview(createRiverLineFeatureCollection(nextPaths), nextPaths, {
      fitMap: options?.fitMap,
    });
    setActiveSelection(options?.activeSelection ?? getDefaultActiveRiverLineSelection(nextPaths));

    if (options?.resetHistory) {
      setLineHistory([]);
    }
  };

  const commitLinePaths = (
    nextPaths: RiverLinePath[],
    options?: { fitMap?: boolean; activeSelection?: RiverLineSelection | null },
  ) => {
    setLineHistory((history) => [...history, linePaths]);
    applyLinePaths(nextPaths, options);
  };

  const handleAddCoordinate = (coordinate: RiverLineCoordinate) => {
    if (!checkedModeEdit) {
      return;
    }

    setSelectedDeviceIds([]);

    const fallbackSelection = activeSelection ?? getDefaultActiveRiverLineSelection(linePaths);
    if (!fallbackSelection) {
      commitLinePaths([[coordinate]], {
        activeSelection: {
          branchIndex: 0,
          coordinateIndex: 0,
        },
      });
      return;
    }

    const path = linePaths[fallbackSelection.branchIndex];
    const anchor = path?.[fallbackSelection.coordinateIndex];
    if (!path || !anchor) {
      commitLinePaths([[coordinate]], {
        activeSelection: {
          branchIndex: 0,
          coordinateIndex: 0,
        },
      });
      return;
    }

    if (anchor[0] === coordinate[0] && anchor[1] === coordinate[1]) {
      return;
    }

    if (fallbackSelection.coordinateIndex === path.length - 1) {
      const nextPaths = linePaths.map((currentPath, branchIndex) => {
        return branchIndex === fallbackSelection.branchIndex ? [...currentPath, coordinate] : currentPath;
      });

      commitLinePaths(nextPaths, {
        activeSelection: {
          branchIndex: fallbackSelection.branchIndex,
          coordinateIndex: path.length,
        },
      });
      return;
    }

    const nextPaths = [...linePaths, [anchor, coordinate]];
    commitLinePaths(nextPaths, {
      activeSelection: {
        branchIndex: nextPaths.length - 1,
        coordinateIndex: 1,
      },
    });
  };

  const handleButtonUndoLastPoint = () => {
    const previousPaths = lineHistory[lineHistory.length - 1];
    if (!previousPaths) {
      return;
    }

    setSelectedDeviceIds([]);
    setLineHistory((history) => history.slice(0, -1));
    applyLinePaths(previousPaths, {
      activeSelection: getDefaultActiveRiverLineSelection(previousPaths),
    });
  };

  const handleButtonClearLine = () => {
    setSelectedDeviceIds([]);
    commitLinePaths([], { activeSelection: null });
  };

  const handleButtonDeleteSelectedPoint = () => {
    if (!checkedModeEdit || !activeSelection) {
      return;
    }

    const currentPath = linePaths[activeSelection.branchIndex];
    if (!currentPath?.[activeSelection.coordinateIndex]) {
      return;
    }

    setSelectedDeviceIds([]);

    const nextPath = currentPath.filter((_, index) => index !== activeSelection.coordinateIndex);
    const nextPaths = linePaths
      .map((path, branchIndex) => {
        return branchIndex === activeSelection.branchIndex ? nextPath : path;
      })
      .filter((path) => path.length > 0);

    commitLinePaths(nextPaths, {
      activeSelection: getDefaultActiveRiverLineSelection(nextPaths),
    });
  };

  const handleSelectCoordinate = (selection: RiverLineSelection) => {
    if (!checkedModeEdit) {
      return;
    }

    setSelectedDeviceIds([]);
    setActiveSelection(selection);
  };

  const handleSelectDevice = (device: RiverDeviceMarker) => {
    if (!checkedModeEdit) {
      return;
    }

    const nextSelectedDeviceIds = selectedDeviceIds.includes(device.id)
      ? selectedDeviceIds.filter((deviceId) => deviceId !== device.id)
      : [...selectedDeviceIds.slice(-1), device.id];

    setSelectedDeviceIds(nextSelectedDeviceIds);

    if (nextSelectedDeviceIds.length === 2) {
      const nextPath = nextSelectedDeviceIds
        .map((deviceId) => areaDevices.find((item) => item.id === deviceId)?.coordinate)
        .filter((coordinate): coordinate is RiverLineCoordinate => Boolean(coordinate));

      if (nextPath.length === 2) {
        setDrawingEnabled(false);
        const nextPaths = [...linePaths, nextPath];
        commitLinePaths(nextPaths, {
          activeSelection: {
            branchIndex: nextPaths.length - 1,
            coordinateIndex: 1,
          },
        });
      }
    }
  };

  const handleButtonPreview = async () => {
    try {
      const file = eInputFile.current?.files?.[0];
      if (!file) {
        toasting({
          children: 'GeoJSON file is required.',
          containerProps: { className: 'border-red-600' },
        });
        return;
      }

      setGeoJsonStatus('loading');
      setSelectedDeviceIds([]);

      const value = await file.text();
      const nextGeoJson = JSON.parse(value) as GeoJsonObject;
      const nextPaths = extractRiverLinePaths(nextGeoJson);

      if (!nextPaths.some((path) => path.length >= 2)) {
        throw new Error('GeoJSON must contain at least one LineString.');
      }

      setDrawingEnabled(false);
      syncGeoJsonPreview(nextGeoJson, nextPaths, { fitMap: true });
      setActiveSelection(getDefaultActiveRiverLineSelection(nextPaths));
      setLineHistory([]);
    } catch (error) {
      console.error('Error handling preview:', error);
      setGeoJsonStatus('error');
      toasting({
        children: getErrorMessage(error, 'GeoJSON load failed.'),
        containerProps: { className: 'border-red-600' },
      });
    }
  };

  const handleButtonSubmit = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (!eForm.current?.checkValidity()) {
      return eForm.current?.reportValidity();
    }

    if (!linePaths.some((path) => path.length >= 2)) {
      setGeoJsonStatus('error');
      toasting({
        children: 'Create at least one line before saving.',
        containerProps: { className: 'border-red-600' },
      });
      return;
    }

    if (eButtonSubmit.current) {
      eButtonSubmit.current.disabled = true;
    }

    const riverData = {
      RIVER_ID: data?.river?.RIVER_ID.S,
      RIVER_NAME: eTextName.current?.value || '',
      REGION_NAME: eSelectAreaName.current?.value || '',
    };

    try {
      const filename = createRiverGeoJsonFilename(riverData.RIVER_NAME);
      const key = `${riverData.REGION_NAME}_${riverData.RIVER_NAME}_${filename}`;
      const body = stringifyRiverGeoJson(geoJsonData);

      const presignedResponse = await fget<unknown, { url: string }>(`api/s3/put-geojson-url?key=${key}`);
      const presignedUrl = presignedResponse[1].url;

      await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/geo+json',
        },
        body,
      });

      await fput(`api/rivers/${id}`, {
        body: {
          river: {
            RIVER_NAME: riverData.RIVER_NAME,
            REGION_NAME: riverData.REGION_NAME,
            COORDINATE_DATA: key,
          },
        },
      });

      navigate('..');
    } catch (error) {
      console.error('Error saving river GeoJSON:', error);
      setGeoJsonStatus('error');
      toasting({
        children: getErrorMessage(error, 'River GeoJSON update failed.'),
        containerProps: { className: 'border-red-600' },
      });
    } finally {
      if (eButtonSubmit.current) {
        eButtonSubmit.current.disabled = false;
      }
    }
  };

  const handleButtonDelete = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    if (!eForm.current?.checkValidity()) {
      return eForm.current?.reportValidity();
    }

    try {
      await fdelete<DeleteRiverReq>(`api/rivers/${id}`);
      navigate('..');
    } catch (error) {
      console.error('Error deleting river:', error);
      toasting({
        children: getErrorMessage(error, 'Delete failed.'),
        containerProps: { className: 'border-red-600' },
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchRiver = async (riverId?: string) => {
          if (!riverId) {
            return {};
          }

          const res = await fget<unknown, ResponseGetDocument<RiverEntity>>(`api/rivers/${riverId}`);
          const item = res?.[1].Item;
          if (!item) {
            return {};
          }

          return { river: item };
        };

        const [areaResponse, riverResponse] = await Promise.all([
          fget<unknown, ResponseListDocuments<AreaEntity>>('api/areas'),
          fetchRiver(id),
        ]);

        setData(() => ({ areas: areaResponse[1], river: riverResponse.river }));
        setSelectedRegionName(riverResponse.river?.REGION_NAME?.S || '');

        const s3CoordinateData = riverResponse.river?.COORDINATE_DATA?.S;
        if (!s3CoordinateData) {
          syncGeoJsonPreview(EMPTY_GEOJSON, []);
          setActiveSelection(null);
          setLineHistory([]);
          return;
        }

        setGeoJsonStatus('loading');
        const res = await fget<unknown, { url: string }>(`api/s3/get-geojson-url?key=${s3CoordinateData}`);
        const url = res?.[1].url;
        const geoJsonRes = await fetch(url);
        const nextGeoJson = (await geoJsonRes.json()) as GeoJsonObject;
        const nextPaths = extractRiverLinePaths(nextGeoJson);

        if (!nextPaths.some((path) => path.length >= 2)) {
          throw new Error('Saved GeoJSON has no valid LineString.');
        }

        syncGeoJsonPreview(nextGeoJson, nextPaths, { fitMap: true });
        setActiveSelection(getDefaultActiveRiverLineSelection(nextPaths));
        setLineHistory([]);
      } catch (error) {
        console.error('Error fetching data:', error);
        setGeoJsonStatus('error');
        toasting({
          children: getErrorMessage(error, 'River GeoJSON load failed.'),
          containerProps: { className: 'border-red-600' },
        });
      }
    };

    if (!id) {
      return;
    }

    void fetchData();
  }, [id]);

  useEffect(() => {
    const fetchAreaDevices = async () => {
      if (!selectedRegionName) {
        setAreaDevices([]);
        setSelectedDeviceIds([]);
        return;
      }

      try {
        const response = await fget<unknown, { Items?: DeviceDocument[] }>(
          `api/deviceByArea?AREA_NAME=${encodeURIComponent(selectedRegionName)}`,
        );
        const devices = (response[1]?.Items || [])
          .map((device) => {
            const coordinate = parseDeviceCoordinate(device.DEVICE_COORDINATE?.S);
            if (!coordinate) {
              return null;
            }

            return {
              id: device.DEVICE_ID?.S || '',
              name: device.DEVICE_NAME?.S || 'device',
              type: device.DEVICE_TYPE?.S || '',
              areaName: device.AREA_NAME?.S || selectedRegionName,
              coordinate,
            } satisfies RiverDeviceMarker;
          })
          .filter((device): device is RiverDeviceMarker => Boolean(device));

        const hydroVenusDevices = devices.filter((device) => isHydroVenusDevice(device.type, device.name));
        setAreaDevices(hydroVenusDevices.length > 0 ? hydroVenusDevices : devices);
        setSelectedDeviceIds([]);
      } catch (error) {
        console.error('Error fetching devices by area:', error);
        setAreaDevices([]);
        setSelectedDeviceIds([]);
      }
    };

    void fetchAreaDevices();
  }, [selectedRegionName]);

  useEffect(() => {
    if (!checkedModeEdit) {
      setDrawingEnabled(false);
      setSelectedDeviceIds([]);
    }
  }, [checkedModeEdit]);

  const totalPointCount = getTotalRiverPointCount(linePaths);

  const renderGeoJsonStatus = () => {
    if (geoJsonStatus === 'loading') {
      return (
        <>
          <svg
            className="-mt-1 mr-2 inline-block h-4 w-4 animate-spin text-yellow-500"
            viewBox="0 0 24 24"
            strokeWidth="3"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" />
            <path d="M4.05 11a8 8 0 1 1 .5 4m-.5 5v-5h5" />
          </svg>
          <span className="font-light">GeoJSON loading</span>
        </>
      );
    }

    if (geoJsonStatus === 'success') {
      return (
        <>
          <svg
            className="-mt-1 mr-2 inline-block h-4 w-4 text-green-500"
            viewBox="0 0 24 24"
            strokeWidth={3}
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" />
            <path d="M5 12l5 5l10 -10" />
          </svg>
          <span className="font-light">GeoJSON ready</span>
        </>
      );
    }

    if (geoJsonStatus === 'error') {
      return (
        <>
          <svg
            className="-mt-1 mr-2 inline-block h-4 w-4 text-red-500"
            viewBox="0 0 24 24"
            strokeWidth={3}
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path stroke="none" d="M0 0h24v24H0z" />
            <path d="M12 8v4m0 4h.01" />
            <path d="M10.29 3.86l-7.39 12.8a1 1 0 0 0 .87 1.5h14.46a1 1 0 0 0 .87-1.5l-7.24-12.8a1 1 0 0 0 -1.74 0z" />
          </svg>
          <span className="font-light">GeoJSON error</span>
        </>
      );
    }

    return (
      <>
        <svg
          className="-mt-1 mr-2 inline-block h-4 w-4 text-slate-400"
          viewBox="0 0 24 24"
          strokeWidth={2.2}
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" />
          <path d="M7 7h10v10H7z" />
          <path d="M10 10h4v4h-4z" />
        </svg>
        <span className="font-light">No GeoJSON yet</span>
      </>
    );
  };

  return data?.river && data.areas ? (
    <form className="flex flex-col px-4 pt-12 md:px-12 md:py-8" ref={eForm}>
      <h1 className="mb-2 whitespace-nowrap text-2xl">河川座標データ編集</h1>
      <span className="w-full border-b border-slate-800"></span>

      <div className="mt-4 flex basis-full justify-end">
        <Checkbox
          className="hidden"
          onChange={(event) => setCheckedModeEdit(event.target.checked)}
          defaultChecked={checkedModeEdit}
          ref={eButtonModeEdit}
          containerProps={{
            className: 'ml-auto border border-gray-500',
          }}
          labelProps={{
            children: '編集モード',
            className: `select-none p-2 ${checkedModeEdit ? 'bg-black text-white' : ''}`,
          }}
        />
      </div>

      <div className="mt-4 flex flex-nowrap gap-8">
        <div className="flex w-2/3 flex-row flex-wrap justify-between gap-8">
          <TextInput
            labelProps={{
              children: 'ID',
            }}
            type="text"
            containerProps={{
              className: 'w-1/3 basis-full',
              style: {
                minWidth: '24rem',
              },
            }}
            disabled
            readOnly
            required
            defaultValue={data.river.RIVER_ID.S}
          />

          <TextInput
            labelProps={{
              children: '河川名',
            }}
            type="text"
            containerProps={{
              className: 'w-1/3 basis-full',
              style: {
                minWidth: '24rem',
              },
            }}
            required
            ref={eTextName}
            defaultValue={data.river.RIVER_NAME.S}
            disabled={!checkedModeEdit}
          />

          <Select
            value={selectedRegionName}
            labelProps={{ value: '地域名' }}
            containerProps={{
              className: 'w-1/3 basis-full',
              style: {
                minWidth: '24rem',
              },
            }}
            required
            ref={eSelectAreaName}
            onChange={(event) => setSelectedRegionName(event.target.value)}
            disabled={!checkedModeEdit}>
            {data.areas.Items?.map((area, index) => (
              <option key={`Option-${index}`} value={area.AREA_NAME.S}>
                {area.AREA_NAME.S}
              </option>
            ))}
          </Select>

          {checkedModeEdit && (
            <>
              <div className="basis-full rounded-md border border-slate-200 bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-800">地図上でGeoJSONを編集</div>
                <p className="mt-2 text-xs leading-5 text-slate-600">
                  地図をクリックして線を作成できます。途中の点を選択してから地図をクリックすると、その点から新しい分岐を追加できます。
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    aria-pressed={drawingEnabled}
                    className={`rounded-md px-4 py-2 text-xs text-white ${drawingEnabled ? 'bg-teal-600' : 'bg-blue-500'}`}
                    onClick={() => setDrawingEnabled((prev) => !prev)}>
                    {drawingEnabled ? '描画中' : '描画開始'}
                  </button>
                  <Button
                    type="button"
                    className="border border-amber-300 bg-amber-50 text-xs text-amber-900 disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-100"
                    onClick={handleButtonUndoLastPoint}
                    disabled={!lineHistory.length}>
                    1操作戻す
                  </Button>
                  <Button
                    type="button"
                    className="border border-sky-300 bg-sky-50 text-xs text-sky-900 disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-100"
                    onClick={handleButtonClearLine}
                    disabled={!linePaths.length}>
                    全消去
                  </Button>
                  <Button
                    type="button"
                    className="border border-rose-300 bg-rose-50 text-xs text-rose-700 disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-100"
                    onClick={handleButtonDeleteSelectedPoint}
                    disabled={!activeSelection}>
                    選択点を削除
                  </Button>
                </div>
                <div className="mt-3 text-xs text-slate-600">
                  分岐数 {linePaths.length} / 点数 {totalPointCount}
                </div>
                <div className="mt-2 text-xs text-slate-600">
                  保存条件: 2点以上のラインを1本以上作成してください。
                </div>
                {activeSelection ? (
                  <div className="mt-2 text-xs font-medium text-orange-700">
                    選択中: 分岐 {activeSelection.branchIndex + 1} / 点 {activeSelection.coordinateIndex + 1}
                  </div>
                ) : null}
                <div className="mt-2 text-xs text-slate-500">
                  表示中の観測点: {areaDevices.length}
                  {selectedDeviceIds.length ? ` / 選択中: ${selectedDeviceIds.length}` : ''}
                </div>
              </div>
              <div className="basis-full flex flex-wrap items-end gap-3">
                <InputFile
                  containerProps={{
                    className: 'mr-3 text-xs',
                  }}
                  label="既存のGeoJSONを読み込む"
                  ref={eInputFile}>
                  <div className="inline-flex flex-nowrap items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.75}
                      stroke="currentColor"
                      className="mr-2 h-4 w-4">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                      />
                    </svg>
                    アップロード
                  </div>
                </InputFile>
                <Button type="button" className="text-xs" onClick={handleButtonPreview}>
                  プレビュー
                </Button>
              </div>
            </>
          )}

          <span className="basis-full text-xs font-light">GeoJSONプレビュー</span>

          <TextArea
            containerProps={{
              className: 'basis-full',
              style: {
                minWidth: '24rem',
              },
            }}
            className="custom-scrollbar-1"
            ref={eTextAreaJson}
            disabled>
            <div>{renderGeoJsonStatus()}</div>
          </TextArea>

          {checkedModeEdit && (
            <>
              <Button
                className="w-full basis-full justify-center bg-black text-xs text-white"
                style={{
                  minWidth: '256px',
                }}
                type="submit"
                onClick={handleButtonSubmit}
                ref={eButtonSubmit}>
                更新
              </Button>
              <Button
                className="w-full basis-full justify-center rounded-md border bg-transparent text-xs text-orange-600"
                style={{
                  minWidth: '256px',
                }}
                type="submit"
                onClick={handleButtonDelete}
                ref={eButtonDelete}>
                削除
              </Button>
            </>
          )}
          <Link
            to=".."
            className={
              'mx-auto w-full basis-full rounded-md border border-gray-600 text-center ' +
              (eButtonModeEdit.current?.checked ? 'p-2.5' : 'pt-1')
            }
            style={{
              minWidth: '256px',
            }}>
            {eButtonModeEdit.current?.checked ? 'キャンセル' : '戻る'}
          </Link>
        </div>

        <div
          className="relative"
          style={{
            width: '20vw',
            minWidth: '48rem',
            flexBasis: '100%',
          }}>
          <MapContainer
            attributionControl={false}
            minZoom={5}
            zoom={9}
            center={[33.9195, 133.1811]}
            style={{
              height: '80vh',
              width: '100%',
              border: '0.25rem solid',
            }}
            ref={map}>
            <TileLayer {...MAPBOX_TILE_LAYER_PROPS} ref={baseTileLayer} maxNativeZoom={18} />
            <Control position="topright">
              <div className="rounded-md bg-white/90 p-2 shadow-md backdrop-blur-sm">
                <SelectBoxBaseLayer baseTileLayer={baseTileLayer} />
              </div>
            </Control>

            <GeoJSON ref={eGeoJSON} data={EMPTY_GEOJSON} />
            <RiverDeviceMarkerLayer
              devices={areaDevices}
              selectedDeviceIds={selectedDeviceIds}
              onSelectDevice={handleSelectDevice}
            />
            <RiverLineDrawingLayer
              paths={linePaths}
              drawingEnabled={checkedModeEdit && drawingEnabled}
              activeSelection={activeSelection}
              onAddCoordinate={handleAddCoordinate}
              onSelectCoordinate={handleSelectCoordinate}
            />
          </MapContainer>
        </div>
      </div>
    </form>
  ) : (
    <></>
  );
}
